import React, { useId, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useFetch } from '../../hooks/useFetch';
import {
  getUrlParamValue,
  setUrlParamValue,
  updateEndpointWithQueryString,
  setUrlParams
} from '../../helpers/queryString';

import PressReleaseCard from './card';
import Pagination from '../pagination';
import Loader from '../listing-states/loader';
import Error from '../listing-states/error';

interface PressReleaseListItems {
  publishMonth: string;
  publishDay: string;
  publishYear: string;
  title: string;
  url: string;
  teaser: string;
}

interface PressReleaseResponse {
  firstResult: number;
  lastResult: number;
  totalResults: number;
  totalPages: number;
  items: PressReleaseListItems[];
}

interface YearOptions {
  label: string;
  value: string;
}

function PressReleaseListing({
  yearLabel,
  yearDefaultValue,
  yearOptions,
  url,
  loadingLabel,
  noResultsMessage,
  noResultsKeywordMessage,
  noResultsAdditionalMessage,
  errorMessage,
  nextPageLabel,
  previousPageLabel,
  pageLabel
}: InferProps<typeof PressReleaseListing.propTypes>) {
  const yearSelectOptions = JSON.parse(yearOptions);
  const selectId = useId();

  const yearParam = getUrlParamValue('year');
  const pageParam = getUrlParamValue('page');

  const [year, setYear] = useState(yearParam || yearDefaultValue);
  const [page, setPage] = useState(Number(pageParam) || 1);

  const [endpoint, setEndpoint] = useState(updateEndpointWithQueryString(url));

  const { data, error } = useFetch<PressReleaseResponse>(endpoint);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = event.target.value;
    setYear(newYear);
    setPage(1);
    setUrlParams({ year: newYear, page: '1' });

    setEndpoint(updateEndpointWithQueryString(url));
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    setUrlParamValue('page', page.toString());
    setEndpoint(updateEndpointWithQueryString(url));
  };

  return (
    <div className="container">
      <div className="press-release-listing__header">
        <div
          className="press-release-listing__summary"
          role="alert"
          aria-live="assertive"
        >
          Showing <strong>{data?.firstResult || 0}</strong> -{' '}
          <strong>{data?.lastResult || 0}</strong> of{' '}
          <strong>{data?.totalResults || 0}</strong> results
        </div>
        <div className="field field--select ">
          <label htmlFor={selectId} className="field__label">
            {yearLabel}
          </label>
          <select
            onChange={handleYearChange}
            value={year}
            name="select"
            id={selectId}
            className="field__select"
          >
            {yearSelectOptions.map((option: YearOptions, index: number) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <Error
          errorMessage={errorMessage}
          noResultsMessage={noResultsMessage}
          noResultsKeywordMessage={noResultsKeywordMessage}
          showError={true}
        />
      ) : (
        <>
          {!data ? (
            <Loader loaderText={loadingLabel} />
          ) : (
            <>
              {data.items.length < 1 ? (
                <Error
                  errorMessage={errorMessage}
                  noResultsMessage={noResultsMessage}
                  noResultsKeywordMessage={noResultsKeywordMessage}
                  noResultsAdditionalMessage={
                    noResultsAdditionalMessage as string
                  }
                />
              ) : (
                <>
                  <ul className="press-release-listing__list" role="list">
                    {data.items.map((item, index) => (
                      <li key={index} className="press-release-listing__item">
                        <PressReleaseCard {...item} />
                      </li>
                    ))}
                  </ul>
                  {data.totalPages > 1 && (
                    <Pagination
                      currentPage={page}
                      totalPages={data.totalPages}
                      onPageChange={handlePageChange}
                      nextPageLabel={nextPageLabel}
                      previousPageLabel={previousPageLabel}
                      pageLabel={pageLabel}
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

PressReleaseListing.propTypes = {
  yearLabel: PropTypes.string.isRequired,
  yearDefaultValue: PropTypes.string.isRequired,
  yearOptions: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  loadingLabel: PropTypes.string.isRequired,
  noResultsMessage: PropTypes.string.isRequired,
  noResultsKeywordMessage: PropTypes.string.isRequired,
  noResultsAdditionalMessage: PropTypes.string,
  errorMessage: PropTypes.string.isRequired,
  nextPageLabel: PropTypes.string.isRequired,
  previousPageLabel: PropTypes.string.isRequired,
  pageLabel: PropTypes.string.isRequired
};

export default PressReleaseListing;
